import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'

/**
 * Generic modal component for displaying overlay content
 * Uses React Portal to render outside of parent DOM hierarchy
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to call when modal should close
 * @param {React.ReactNode} children - Content to display in the modal
 * @param {string} className - Additional CSS classes for the modal content container
 * @param {boolean} closeOnBackdropClick - Whether clicking the backdrop closes the modal (default: true)
 * @param {boolean} showCloseButton - Whether to show a close button (default: false)
 */
export const Modal = ({
  isOpen,
  onClose,
  children,
  className = '',
  closeOnBackdropClick = true,
  showCloseButton = false,
}) => {
  useEffect(() => {
    // Create modal root if it doesn't exist
    if (!document.getElementById('modal-root')) {
      const modalRoot = document.createElement('div')
      modalRoot.id = 'modal-root'
      document.body.appendChild(modalRoot)
    }
  }, [])

  if (!isOpen) return null

  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose()
    }
  }

  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999] px-4 animate-fade-in"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={handleBackdropClick}
    >
      <div
        className={`rounded-lg p-6 max-w-md w-full relative ${className}`}
        style={{
          background: 'rgba(10, 10, 10, 0.9)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 20px 40px 0 rgba(0, 0, 0, 0.6), inset 0 1px 0 0 rgba(255, 255, 255, 0.1)',
        }}
      >
        {showCloseButton && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
        {children}
      </div>
    </div>
  )

  // Get modal root element
  const modalRoot = document.getElementById('modal-root')

  // If modal root exists, render using portal
  if (modalRoot) {
    return createPortal(modalContent, modalRoot)
  }

  // Fallback to regular rendering if modal root doesn't exist
  return modalContent
}

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  closeOnBackdropClick: PropTypes.bool,
  showCloseButton: PropTypes.bool,
}
